import { articleService, courseService, trailService } from '../../services/api';

/**
 * Resolve os parâmetros das rotas do tour com o que a pessoa usa de verdade:
 * o curso, tópico e aula mais recentes (ou o primeiro liberado), a trilha em andamento
 * (ou a primeira do catálogo) e o artigo mais recente. Cada parâmetro é buscado uma vez por tour.
 */
export function createTourContext() {
  const cache = {};
  const once = (key, load) => {
    if (!cache[key]) cache[key] = load().catch(() => null);
    return cache[key];
  };

  const overview = () => once('overview', () => courseService.overview());

  const course = () => once('course', async () => {
    const list = (await overview()) || [];
    if (!list.length) return null;
    let saved = null;
    try { saved = localStorage.getItem('currentCourseSlug'); } catch { /* sem storage */ }
    const recent = [...list].filter(c => c.lastLesson)
      .sort((a, b) => String(b.lastLesson.viewedAt).localeCompare(String(a.lastLesson.viewedAt)))[0];
    return list.find(c => c.slug === saved) || recent || list[0];
  });

  const lesson = () => once('lesson', async () => {
    const c = await course();
    if (!c) return null;
    if (c.lastLesson) return { topicId: c.lastLesson.topicId, lessonId: c.lastLesson.lessonId };
    const topics = (await courseService.getTopicsByCourse(c.slug, true)) || [];
    const first = [...topics].sort((a, b) => (a.fase || 0) - (b.fase || 0)).find(t => t.lessons?.length) || topics[0];
    return first ? { topicId: first.id, lessonId: first.lessons?.[0]?.id ?? null } : null;
  });

  const trail = () => once('trail', async () => {
    const today = await trailService.today().catch(() => null);
    if (today?.slug) return today.slug;
    const data = await trailService.catalog();
    const trails = Array.isArray(data) ? data : (data?.trails || []);
    const pick = trails.find(t => t.status !== 'draft' && t.hasStages) || trails[0];
    return pick?.slug || null;
  });

  const article = () => once('article', async () => {
    const data = await articleService.list({ limit: 1 });
    const items = Array.isArray(data) ? data : (data?.items || []);
    return items[0] || null;
  });

  const params = {
    slug: async () => (await course())?.slug,
    topico: async () => (await lesson())?.topicId,
    aula: async () => (await lesson())?.lessonId,
    trilha: trail,
    artigo: async () => (await article())?.slug,
    artigoId: async () => (await article())?.id,
  };

  return {
    /** Caminho concreto da rota, ou null se algum parâmetro não existe (o passo é pulado). */
    async resolve(route) {
      const names = route.match(/:[A-Za-z]+/g) || [];
      let path = route;
      for (const name of names) {
        const value = await params[name.slice(1)]?.();
        if (value == null || value === '') return null;
        path = path.replace(name, encodeURIComponent(value));
      }
      return path;
    },
    /** Última aula vista, para o "Continuar de onde parei" da conclusão. */
    async resumePath() {
      const c = await course();
      if (!c) return '/cursos';
      if (c.lastLesson) return `/curso/${c.slug}/estudo/${c.lastLesson.topicId}/aula/${c.lastLesson.lessonId}`;
      return `/curso/${c.slug}/estudo`;
    },
  };
}
